import type { User } from '@/modules/user/userTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { getUserEmail } from '@/services/authentication';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import Papa from 'papaparse';
import type {
	ReportBusiness,
	ReportUserPeriod,
	ResultReport
} from '../@types/types';
import type { ListProps } from '../hooks/useSubordinatedListsData';

const getListName = ({
	listId,
	lists
}: Pick<ReportUserPeriod, 'listId'> & Pick<ExportAsCsvProps, 'lists'>) => {
	const foundList = lists.find(list => list.id === listId);

	if (!listId || !foundList) {
		return 'Overall';
	}

	return foundList.name;
};

const getCurrDate = () => {
	const currDate = new Date();
	const currDateString = `${currDate.toDateString()} ${currDate.toLocaleTimeString()}`;
	return currDateString;
};

type ExportAsCsvProps = {
	report: ResultReport;
	reportUserPeriod: ReportUserPeriod;
	categoryReport: Array<{
		name: string;
		value: number;
		color: string;
	}>;
	lists: ListProps[];
	user: User;
};

export const handleExportAsCsv = ({
	report,
	reportUserPeriod,
	categoryReport,
	user,
	lists
}: ExportAsCsvProps) => {
	let openRate = '-1';

	if (report?.emails?.total !== 0 && report?.emails?.total !== undefined) {
		openRate = ((report?.emails?.opened / report?.emails?.total) * 100)
			.toString()
			.concat('%');
	}

	const exportData: Array<unknown[]> = [
		/* eslint-disable no-sparse-arrays */
		['Overview', , , , , , ,],
		[
			`List Name: ${getListName({ listId: reportUserPeriod.listId, lists })}`,
			,
			,
			,
			,
			,
			,
			,
		],
		[`Date Exported: ${getCurrDate()}`],
		['Businesses Contacted', , , , , , , report.totalContacted],
		['Lists created', , , , , , , report.totalListsCreated],
		[
			'Average RE2 score of contacted businesses',
			,
			,
			,
			,
			,
			,
			report.totalContacted
		],
		['Businesses exported', , , , , , , report.totalBusinessesExported],
		['Open Rate', , , , , , , openRate === '-1' ? 'No emails' : openRate],
		['\r\n'],
		['\r\n'],
		['Categories', , , , , , ,],
		['\r\n']
		/* eslint-enable no-sparse-arrays */
	];

	categoryReport?.forEach(({ name, value }) => {
		// eslint-disable-next-line no-sparse-arrays
		exportData.push([
			name,
			,
			,
			,
			,
			,
			,
			`${((value * 100) / report.totalContacted).toFixed(1)}%`
		]);
	});

	exportData.push(['\r\n'], ['\r\n']);

	exportData.push([
		'Name',
		'RE2 Score',
		'Address',
		'Contacted',
		'Lead',
		'Categories',
		'Phone Number',
		'Email Address',
		'Yelp Star Rating',
		'Yelp Review Count',
		'Staff Info',
		'User Notes'
	]);

	exportData.push(['\r\n']);

	let totalRE2Score = 0;

	report.contactedBusinesses?.forEach((business: ReportBusiness) => {
		totalRE2Score += business.score;
		let email = '';

		if (
			business.notes &&
			business.notes.userAddedData &&
			business?.notes?.userAddedData?.length
		) {
			if (
				business.notes.userAddedData.filter(
					x => x.type === 'email' && x.checked === true
				).length > 0
			) {
				email = business.notes.userAddedData.filter(
					x => x.type === 'email' && x.checked === true
				)[0].data;
			} else if (
				business.facebook?.email &&
				business.notes?.contactType?.emailPrimary
			) {
				email = business.facebook?.email;
			} else if (
				business.web?.email &&
				business.notes?.contactType?.thirdEmail
			) {
				email = business.web.email;
			} else if (
				business.email &&
				business.notes?.contactType?.emailSecondary
			) {
				email = business.email;
			}
		}

		const staffInfo = [];

		if (business?.contactNames) {
			for (let i = 0; i < business?.contactNames?.length; i++) {
				const name = business?.contactNames[i]?.name;
				const title = business?.contactNames[i]?.titles.find(
					(t: string) => t !== 'CONTACT NAME'
				);
				staffInfo.push(name + (title ? ` - ${title}\n` : '\n'));
			}
		}

		const activityNotes = [];

		if (business?.activityNotes) {
			for (let i = 0; i < business?.activityNotes?.length; i++) {
				activityNotes.push(
					`Note: ${business?.activityNotes[i]?.text}\nTime: ${business?.activityNotes[i]?.updatedAt}\n`
				);
			}
		}

		if (email === '') {
			const primaryEmailAddress = () => {
				if (business.facebook?.email) return business.facebook.email;
				if (!business.facebook?.email && business.email)
					return business.email;
				if (
					!business.facebook?.email &&
					!business.email &&
					business.web?.email
				)
					return business.web.email;

				return '';
			};

			email = primaryEmailAddress();
		}

		exportData.push([
			business.name
				.replace('ː', ':')
				.replace('’', "'")
				.replace(' ', ' ')
				.replace('˂', '<')
				.replace('˃', '>'),
			Math.floor(business.score || 0),
			business.address.address,
			business.notes && business.notes.isContacted ? 'YES' : 'NO',
			business.notes && business.notes.lead ? 'YES' : 'NO',
			business.displayCategory?.split("\n").join(", "),
			business.phone.display,
			email,
			business.yelp.rating,
			business.yelp.reviews,
			business.contactNames ? staffInfo : [],
			business.activityNotes ? activityNotes : ''
		]);
	});

	const averageRe2Score = Number(
		totalRE2Score / report.totalContacted
	).toFixed(1);

	exportData[5][exportData[5].length - 1] = averageRe2Score;

	FileSaver.saveAs(
		new Blob(
			[
				Papa.unparse(exportData, {
					header: false
				})
			],
			{
				type: 'text/csv;charset=utf-8'
			}
		),

		`${reportUserPeriod?.userEmail || getUserEmail()} List [${getListName({ listId: reportUserPeriod.listId, lists })}] Dates [${format(reportUserPeriod.startDate, 'MMM d, y')} - ${format(
			reportUserPeriod.endDate,
			'MMM d, y'
		)}].csv`
	);

	if (user && user.email) {
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User exported Activity Report',
			properties: {
				exportType: '.csv',
				listName: reportUserPeriod?.listName,
				listId: reportUserPeriod?.listId,
				userEmail: reportUserPeriod?.userEmail,
				startDate: format(reportUserPeriod.startDate, 'MMM d, y'),
				endDate: format(reportUserPeriod.endDate, 'MMM d, y')
			}
		});
	}
};
